// Ticket pieces shared by the client Support view (Tickets.tsx) and the admin
// inbox (TicketInbox.tsx): the New-ticket dialog, the target-clock chip and
// field, and the conversation thread + composer (TicketThread.dc.html).
import { useEffect, useRef, useState } from "react";
import { Button, Dialog, Field, Icon, Input, Select } from "../../ds/DS";
import { api } from "../../lib/api";
import type { TicketClock } from "../data";
import { PRIORITIES, TICKET_CATEGORIES } from "../tone";
import type { PageProps } from "../Portal";
import { initials, threadTime, type ThreadFile, type ThreadItem, type ThreadSend } from "../ticketThread";
import { useMedia } from "../useMedia";

/* ---------- target clock ---------- */
// The API derives tone/label/progress (functions/_lib/sla.ts); these only
// render it. Waiting on client shows the pause icon and a muted bar.

/** List-row / board-card chip (20px, radius 6). Nothing when there's no clock. */
export function ClockChip({ c }: { c: TicketClock }) {
  if (!c.kind || !c.short) return null;
  return (
    <span className="ibx-clock" data-tone={c.paused ? "paused" : c.tone}>
      <Icon name={c.paused ? "pause" : "clock"} size={11} />
      {c.short}
    </span>
  );
}

/** Detail field: "First reply" / "Fix target" label, value and a 5px bar. */
export function ClockField({ c }: { c: TicketClock }) {
  return (
    <div className="ibx-field">
      <div className="ibx-flabel">{c.kindLabel || "Target"}</div>
      <div className="ibx-target" data-tone={c.paused ? "paused" : c.tone}>
        {c.paused ? <Icon name="pause" size={13} /> : null}
        <span className="ibx-target-val mono">{c.kind ? c.label : "—"}</span>
        <div className="ibx-bar">
          <div style={{ width: `${c.kind ? c.pct : 0}%` }} />
        </div>
      </div>
    </div>
  );
}

/* ---------- New ticket ---------- */
// Per-category presentation for the New-ticket type tiles. Icons are drawn from
// the curated Icon registry; falls back gracefully for any category without an
// entry (label only).
const TYPE_META: Record<string, { icon: string; sub: string }> = {
  Issue: { icon: "alert-triangle", sub: "Something's broken or not working" },
  Question: { icon: "message-square", sub: "You need an answer" },
  Change: { icon: "git-branch", sub: "A tweak or addition" },
};

// Ticket type picker rendered as selectable tiles (replaces the plain dropdown).
// Shared by the client and admin New-ticket dialogs.
export function TypeTiles({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="type-tiles" role="radiogroup" aria-label="Ticket type">
      {TICKET_CATEGORIES.map((cat) => {
        const meta = TYPE_META[cat];
        const selected = value === cat;
        return (
          <button
            key={cat}
            type="button"
            role="radio"
            aria-checked={selected}
            className={"type-tile" + (selected ? " is-selected" : "")}
            onClick={() => onChange(cat)}
          >
            {meta ? <Icon name={meta.icon} size={18} /> : null}
            <span className="type-tile-label">{cat}</span>
            {meta ? <span className="type-tile-sub">{meta.sub}</span> : null}
          </button>
        );
      })}
    </div>
  );
}

const ENVIRONMENTS = ["Production", "Sandbox", "Not sure"];
const AS_TEAM = "Agile Software (no client)";

interface ClientUser {
  id: string;
  name: string;
  email: string;
  role: string;
}

/** Pre-fill for the dialog, e.g. "Report a problem" on a sandbox release. */
export interface NewTicketPreset {
  subject: string;
  projectId: string;
  releaseId: string | null;
}

export function NewTicket({
  open,
  isAdmin,
  projects,
  defaultPid,
  preset,
  onClose,
  onCreated,
}: {
  open: boolean;
  isAdmin: boolean;
  projects: PageProps["projects"];
  defaultPid: string;
  preset?: NewTicketPreset | null;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const initialProject =
    projects.find((p) => p.id === defaultPid) ?? projects[0];
  const [f, setF] = useState({
    projectName: initialProject?.name ?? "",
    subject: "",
    body: "",
    category: "Issue",
    priority: "Normal",
    environment: "Production",
    person: "",
  });
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [people, setPeople] = useState<ClientUser[]>([]);
  const set = (k: keyof typeof f, v: string) => setF((s) => ({ ...s, [k]: v }));

  // Re-seed the project each time the dialog opens (the scope may have moved),
  // or take the preset's project and subject.
  useEffect(() => {
    if (!open) return;
    const p =
      projects.find((x) => x.id === (preset?.projectId ?? defaultPid)) ?? projects[0];
    setF((s) => ({
      ...s,
      projectName: p?.name ?? s.projectName,
      ...(preset ? { subject: preset.subject, category: "Issue", environment: "Sandbox" } : {}),
    }));
    setTried(false);
    setError("");
  }, [open, defaultPid, projects, preset]);

  // Admin "On behalf of": the client users of the chosen project's client.
  const project = projects.find((p) => p.name === f.projectName);
  const clientId = project?.clientId ?? "";
  useEffect(() => {
    if (!open || !isAdmin || !clientId) return;
    let live = true;
    api
      .get<{ users: ClientUser[] }>(`/clients/${clientId}/users`)
      .then((r) => {
        if (!live) return;
        const list = r.users.filter((u) => u.role === "client");
        setPeople(list);
        setF((s) =>
          list.some((u) => u.id === s.person) ? s : { ...s, person: list[0]?.id ?? "" },
        );
      })
      .catch(() => live && setPeople([]));
    return () => {
      live = false;
    };
  }, [open, isAdmin, clientId]);

  const personLabel = (u: ClientUser) => u.name || u.email;
  // The preset release applies only while its project stays selected.
  const releaseId =
    preset?.releaseId && project?.id === preset.projectId ? preset.releaseId : null;
  const subjectMissing = !f.subject.trim();

  const submit = async () => {
    setTried(true);
    if (!project || subjectMissing) return;
    setBusy(true);
    setError("");
    try {
      const r = await api.post<{ ticket: { id: string } }>(
        `/projects/${project.id}/tickets`,
        {
          category: f.category,
          title: f.subject.trim(),
          body: f.body,
          priority: f.priority.toLowerCase(),
          environment: f.environment,
          channel: "Portal",
          ...(isAdmin && f.person ? { onBehalfOf: f.person } : {}),
          ...(releaseId ? { releaseId } : {}),
        },
      );
      onCreated(r.ticket.id);
      onClose();
      setF((s) => ({ ...s, subject: "", body: "" }));
      setTried(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't raise the ticket. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      title={isAdmin ? "Log a ticket" : "New ticket"}
      description="Tell us what happened and what you expected. Screenshots help."
      accent
      width={580}
      onClose={onClose}
      footer={
        <div className="row-8">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button arrow onClick={submit} disabled={busy}>
            {busy ? "Sending…" : isAdmin ? "Log ticket" : "Raise ticket"}
          </Button>
        </div>
      }
    >
      <div className="stack-16">
        <div className="nt-subject">
          <Field label="Subject" required>
            <Input
              placeholder="Stock levels not updating after refund"
              value={f.subject}
              aria-invalid={tried && subjectMissing}
              className={tried && subjectMissing ? "is-invalid" : undefined}
              onChange={(e) => set("subject", e.target.value)}
            />
          </Field>
          {tried && subjectMissing ? (
            <span className="nt-error">Add a subject so we know what this is about.</span>
          ) : null}
        </div>
        <Field label="Type">
          <TypeTiles
            value={f.category}
            onChange={(v) => set("category", v)}
          />
        </Field>
        <div className="grid-2">
          <Field label="Project">
            <Select
              options={projects.map((p) => p.name)}
              value={f.projectName}
              onChange={(e) => set("projectName", e.target.value)}
            />
          </Field>
          {isAdmin ? (
            <Field label="On behalf of">
              <Select
                options={[...people.map(personLabel), AS_TEAM]}
                value={
                  people.find((u) => u.id === f.person)
                    ? personLabel(people.find((u) => u.id === f.person) as ClientUser)
                    : AS_TEAM
                }
                onChange={(e) =>
                  set("person", people.find((u) => personLabel(u) === e.target.value)?.id ?? "")
                }
              />
            </Field>
          ) : null}
          <Field label="Priority" hint="Urgent means something is down.">
            <Select
              options={PRIORITIES}
              value={f.priority}
              onChange={(e) => set("priority", e.target.value)}
            />
          </Field>
          <Field label="Environment">
            <Select
              options={ENVIRONMENTS}
              value={f.environment}
              onChange={(e) => set("environment", e.target.value)}
            />
          </Field>
        </div>
        <Field label="What's happening" hint="Include order numbers, URLs or times.">
          <Input
            multiline
            rows={4}
            value={f.body}
            onChange={(e) => set("body", e.target.value)}
          />
        </Field>
        {error ? <div className="nt-error">{error}</div> : null}
      </div>
    </Dialog>
  );
}

function FileTile({ f }: { f: ThreadFile }) {
  if (f.preview)
    return (
      <a className="thr-img" href={f.url} download={f.name} title={`Download ${f.name}`}>
        <span className="thr-img-box">
          <img src={f.preview} alt={f.name} loading="lazy" />
        </span>
        <span className="thr-img-foot">
          <span className="thr-file-name">{f.name}</span>
          <span className="thr-file-size mono">{f.size}</span>
        </span>
      </a>
    );
  return (
    <a className="thr-file" href={f.url} download={f.name} title={`Download ${f.name}`}>
      <span className="thr-file-ic">
        <Icon name="file-text" size={14} />
      </span>
      <span className="thr-file-text">
        <span className="thr-file-name">{f.name}</span>
        <span className="thr-file-size mono">{f.size}</span>
      </span>
      <Icon name="download" size={14} />
    </a>
  );
}

const MAX_FILE_BYTES = 10 * 1024 * 1024;
const MAX_FILES = 10;

/**
 * Conversation + composer. `viewer="admin"` adds the Reply / Internal note
 * tabs and "Send & wait on client"; the client view never renders notes (the
 * API doesn't send them either).
 */
export function TicketThread({
  items,
  viewer,
  recipient = "the client",
  hint,
  onSend,
}: {
  items: ThreadItem[];
  viewer: "admin" | "client";
  recipient?: string;
  hint?: string;
  onSend: (m: ThreadSend) => Promise<void>;
}) {
  const isAdmin = viewer === "admin";
  const [mode, setMode] = useState<"reply" | "note">("reply");
  const [draft, setDraft] = useState("");
  const [pending, setPending] = useState<File[]>([]);
  const [fileError, setFileError] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const note = isAdmin && mode === "note";
  const shown = isAdmin ? items : items.filter((it) => it.kind !== "note");

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [shown.length]);

  const addFiles = (list: FileList | null) => {
    if (!list) return;
    const picked = Array.from(list);
    const tooBig = picked.find((f) => f.size > MAX_FILE_BYTES);
    const ok = picked.filter((f) => f.size > 0 && f.size <= MAX_FILE_BYTES);
    setPending((p) => [...p, ...ok].slice(0, MAX_FILES));
    setFileError(
      tooBig
        ? `${tooBig.name} is over 10 MB. Send a smaller file or a link.`
        : pending.length + ok.length > MAX_FILES
          ? `Attach up to ${MAX_FILES} files at a time.`
          : "",
    );
  };

  const send = async (wait: boolean) => {
    const body = draft.trim();
    if ((!body && !pending.length) || busy) return;
    setBusy(true);
    try {
      await onSend({ body, note, wait: wait && !note, files: pending });
      setDraft("");
      setPending([]);
      setFileError("");
    } catch (e) {
      setFileError(e instanceof Error ? e.message : "Couldn't send. Try again.");
    } finally {
      setBusy(false);
    }
  };
  const empty = !draft.trim() && !pending.length;
  // No keyboard-shortcut hint on touch screens.
  const touch = useMedia("(hover: none)");

  const placeholder = note
    ? "Note for yourself. The client never sees this."
    : isAdmin
      ? `Reply to ${recipient}…${touch ? "" : "  (Ctrl+Enter sends)"}`
      : "Add a reply…";
  const hintText =
    fileError ||
    (note
      ? "Not sent to the client"
      : isAdmin
        ? `Emails ${recipient}`
        : (hint ?? "We reply within one working day."));

  return (
    <div className="ibx-thread">
      <div className="ibx-thread-scroll" ref={scrollRef}>
        {shown.map((it) =>
          it.kind === "event" ? (
            <div key={it.key} className="ibx-event">
              <span className="ibx-event-dot" />
              <span>{it.body}</span>
              <span className="ibx-event-at mono">{threadTime(it.at)}</span>
            </div>
          ) : (
            <div key={it.key} className="ibx-msg" data-side={it.side} data-kind={it.kind}>
              <span className="ibx-av mono">{initials(it.who)}</span>
              <div className="ibx-bubble">
                <div className="ibx-bubble-head">
                  {it.kind === "note" ? <Icon name="lock" size={13} /> : null}
                  <span className="ibx-who">{it.who}</span>
                  <span className="ibx-role">
                    {it.kind === "note"
                      ? "Internal note · only Agile Software sees this"
                      : it.side === "team"
                        ? "Agile Software"
                        : "Client"}
                  </span>
                  <span className="ibx-at mono">{threadTime(it.at)}</span>
                </div>
                {it.body ? <p className="ibx-text">{it.body}</p> : null}
                {it.files.length ? (
                  <div className="thr-files">
                    {it.files.map((f) => (
                      <FileTile key={f.id} f={f} />
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          ),
        )}
        {shown.length === 0 ? <div className="hint">Loading the conversation…</div> : null}
      </div>
      <div className="ibx-composer">
        {isAdmin ? (
          <div className="ibx-modes">
            <button
              className={"ibx-mode" + (!note ? " is-on" : "")}
              onClick={() => setMode("reply")}
            >
              <Icon name="reply" size={14} />
              <span>Reply to {recipient}</span>
            </button>
            <button
              className={"ibx-mode ibx-mode--note" + (note ? " is-on" : "")}
              onClick={() => setMode("note")}
            >
              <Icon name="lock" size={14} />
              <span>Internal note</span>
            </button>
          </div>
        ) : null}
        <div className={"ibx-box" + (note ? " is-note" : "")}>
          <textarea
            rows={3}
            value={draft}
            placeholder={placeholder}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                void send(false);
              }
            }}
          />
          {pending.length ? (
            <div className="thr-pending">
              {pending.map((f, i) => (
                <span key={`${f.name}-${i}`} className="thr-chip">
                  <Icon name={f.type.startsWith("image/") ? "image" : "file-text"} size={12} />
                  {f.name}
                  <button
                    type="button"
                    aria-label="Remove attachment"
                    onClick={() => setPending((p) => p.filter((_, j) => j !== i))}
                  >
                    <Icon name="x" size={12} />
                  </button>
                </span>
              ))}
            </div>
          ) : null}
          <div className="ibx-box-foot">
            <input
              ref={fileRef}
              type="file"
              multiple
              hidden
              onChange={(e) => {
                addFiles(e.target.files);
                e.target.value = "";
              }}
            />
            <button type="button" className="thr-attach" onClick={() => fileRef.current?.click()}>
              <Icon name="paperclip" size={14} />
              Attach
            </button>
            <span className={"ibx-hint" + (fileError ? " is-error" : "")}>{hintText}</span>
            <div className="row-8" style={{ marginLeft: "auto" }}>
              {isAdmin && !note ? (
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={empty || busy}
                  onClick={() => void send(true)}
                >
                  Send &amp; wait on client
                </Button>
              ) : null}
              <Button
                size="sm"
                arrow
                disabled={empty || busy}
                onClick={() => void send(false)}
              >
                {note ? "Save note" : "Send reply"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
